'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useDebouncedCallback } from 'use-debounce';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { useState, useTransition } from 'react';



interface Category {
    name: string;
    slug: string;
}

export default function ArticleFilters({ 
    categories, 
    baseUrl = '/articles',
    activeCategorySlug 
}: { 
    categories: Category[], 
    baseUrl?: string,
    activeCategorySlug?: string 
}) {
    const searchParams = useSearchParams();
    const router = useRouter();
    const [isPending, startTransition] = useTransition();

    const currentSearch = searchParams.get('q') || '';
    const currentCategory = searchParams.get('category') || activeCategorySlug || '';

    const handleSearch = useDebouncedCallback((term: string) => {
        // We can update the hash to reflect search state if desired, or keep using query params for search?
        // User asked for "symbolic # tag on frontpage" for KEYWORDS/Categories.
        // Let's stick to query params for Search (?q=...) as that is standard.
        // But for Categories, if we are on the homepage, maybe we use hash?

        // Actually, mixing Hash for state and URL for routing is tricky.
        // If the user wants: http://localhost:3000/#bmw to LOAD BMW content, 
        // that's CLIENT SIDE filtering.
        // If they want http://localhost:3000/bmw (server side) but visually see hashtags...

        // Let's stick to Server Side Filtering (Query Params) for Search.
        const params = new URLSearchParams(searchParams);
        if (term) {
            params.set('q', term);
        } else {
            params.delete('q');
        }
        params.delete('page'); // Reset pagination

        startTransition(() => {
            router.replace(`${baseUrl}?${params.toString()}`, { scroll: false });
        });
    }, 300);

    const handleCategoryChange = (categorySlug: string) => {
        if (categorySlug) {
            const parent = (() => {
                const candidates = categories.filter(p => categorySlug.startsWith(`${p?.slug}-`));
                if (candidates.length === 0) return null;
                return candidates.sort((a, b) => b.slug.length - a.slug.length)[0].slug;
            })();
            if (parent) {
                router.push(`/${parent}/${categorySlug}`);
            } else {
                router.push(`/${categorySlug}`);
            }
        } else {
            router.push('/');
        }
    };

    const clearFilters = () => {
        startTransition(() => {
            router.push('/');
        });
    };

    const hasFilters = currentSearch || currentCategory;

    return (
        <div className="bg-[var(--bg-elevated)] border border-[var(--color-border)] rounded-xl p-4 md:p-6 mb-12 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4 items-center">
                {/* Search Input */}
                <div className="relative w-full md:flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-[var(--color-text-muted)]" />
                    </div>
                    <label htmlFor="article-search" className="sr-only">Search articles</label>
                    <input
                        id="article-search"
                        name="q"
                        type="search"
                        placeholder="Search articles..."
                        className="w-full pl-10 pr-4 py-3 bg-[var(--bg-base)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent outline-none transition-all"
                        defaultValue={currentSearch}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </div>

                {/* Category Dropdown */}
                <div className="w-full md:w-64 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SlidersHorizontal className="h-4 w-4 text-[var(--color-text-muted)]" />
                    </div>
                    {(() => {
                        const uniqueCategories = Array.from(new Map(categories.filter(c => c && c.slug).map(c => [c.slug, c])).values());
                        const parents = uniqueCategories.filter(p => uniqueCategories.some(c => c.slug.startsWith(`${p.slug}-`)));
                        const used = new Set<string>();
                        const childrenMap: Record<string, Category[]> = {};
                        parents.forEach(p => {
                            used.add(p.slug);
                            childrenMap[p.slug] = uniqueCategories.filter(c => c.slug.startsWith(`${p.slug}-`));
                            childrenMap[p.slug].forEach(c => used.add(c.slug));
                        });
                        const others = uniqueCategories.filter(c => !used.has(c.slug));
                        return (
                    <>
                    <label htmlFor="category-select" className="sr-only">Category</label>
                    <select
                        id="category-select"
                        name="category"
                        className="w-full pl-10 pr-4 py-3 bg-[var(--bg-base)] border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] outline-none appearance-none cursor-pointer text-[var(--color-text-main)] themed-select"
                        value={currentCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                        <option value="">All Categories</option>
                        {parents.map((parent) => (
                            <optgroup key={parent.slug} label={parent.name}>
                                <option value={parent.slug}>{parent.name}</option>
                                {childrenMap[parent.slug].map((child) => (
                                    <option key={child.slug} value={child.slug}>
                                        {child.name}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                        {others.length > 0 && (
                            <optgroup label="Other Categories">
                                {others.map((cat) => (
                                    <option key={cat.slug} value={cat.slug}>
                                        {cat.name}
                                    </option>
                                ))}
                            </optgroup>
                        )}
                    </select>
                    </>
                        );
                    })()}
                </div>

                {/* Clear Button */}
                {/* {hasFilters && (
                    <button
                        onClick={clearFilters}
                        className="text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary)] flex items-center gap-1 transition-colors whitespace-nowrap"
                    >
                        <X size={16} /> Clear Filters
                    </button>
                )} */}
            </div>
            {isPending && (
                <div className="h-1 w-full bg-[var(--bg-secondary)] mt-4 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--color-primary)] animate-progress origin-left w-full"></div>
                </div>
            )}
        </div>
    );
}

